import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/tables/Table";
import { Contract } from '@/types';
import { Loader2, Search } from 'lucide-react';
import { useWallet } from '@/contexts/WalletContext';
import { useContracts } from '@/hooks/data/useContracts';

const ActionButton: React.FC<{ onClick: () => void; className: string; children: React.ReactNode }> = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${className}`}>
    {children}
  </button>
);

const TableActions: React.FC<{ 
  contract: Contract;
  onAction: (action: string, contractId: string) => void;
}> = ({ contract, onAction }) => {

  const handleAction = (action: string) => {
    onAction(action, contract.id);
  };
  
  return (
      <div className="space-x-2">
        <ActionButton onClick={() => handleAction('view')} className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/40">View</ActionButton>
      </div>
    );
};

const ContractFactoryRealDataTable = () => {
  const { contracts: allContracts, isLoading, error } = useContracts();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const { isConnected } = useWallet();
  const PAGE_SIZE = 10;

  const filteredContracts = useMemo(() => {
      return allContracts.filter(contract => 
          contract.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contract.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
  }, [allContracts, searchTerm]);

  const displayedContracts = useMemo(() => {
      return filteredContracts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filteredContracts, page]);

  const hasMore = page * PAGE_SIZE < filteredContracts.length;

  if (isLoading && allContracts.length === 0) return (
    <div className="flex justify-center items-center h-48">
        <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
        <p className="ml-4 text-kala-400">Loading Unified Contract Ledger...</p>
    </div>
  );

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Contract Factory</h2>
            <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
                <input
                    type="text"
                    placeholder="Search by name or type..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-72"
                />
            </div>
        </div>

        {error && <div className="p-4 mb-4 bg-yellow-500/10 text-yellow-400 text-center text-sm rounded-lg">Warning: {error}</div>}
        {!isConnected && (
            <div className="p-2 mb-4 bg-kala-900/30 text-kala-500 text-center text-[10px] uppercase tracking-widest border-y border-kala-700">
            Note: Wallet disconnected. On-chain data is unavailable.
            </div>
        )}

        <div className="overflow-x-auto">
            <Table>
                <TableCaption>A list of all created contracts.</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contract Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedContracts.map((contract) => (
                    <TableRow key={contract.id}>
                      <TableCell>{contract.name}</TableCell>
                      <TableCell>{contract.type}</TableCell>
                      <TableCell>{contract.status}</TableCell>
                      <TableCell className="text-right">
                        <TableActions contract={contract} onAction={() => {}} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
            </Table>
        </div>

        {displayedContracts.length === 0 && !isLoading && (
            <div className="text-center p-8 text-kala-500">
                <p>No contracts found for your search term.</p>
            </div>
        )}

        <div className="flex justify-between items-center mt-6">
            <button 
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || isLoading}
            className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
            >
            Previous
            </button>
            <span className="text-kala-400">Page {page} of {Math.ceil(filteredContracts.length / PAGE_SIZE)}</span>
            <button 
            onClick={() => setPage(p => p + 1)}
            disabled={!hasMore || isLoading}
            className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
            >
            Next
            </button>
      </div>
    </div>
  );
};

export default ContractFactoryRealDataTable;
